import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { toast } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LayoutGrid, Check, X, Search, Edit, Loader2, Move } from 'lucide-react';
import { ReactPhotoEditor } from 'react-photo-editor';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { usePatientList, usePatientProfile, useCreateMultiPhotoCollage } from '../hooks/useApi';
import { useDebounce } from '../hooks/useDebounce';
import { collageLayouts } from '../components/Collegelayout';
import LayoutSelector from '../components/Layoutselector';

/* ---------------- HELPER TO PARSE GRID LAYOUTS ---------------- */

const parseGridTemplate = (template, totalSize) => {
  const parts = template.split(" ").map(p => parseFloat(p));
  const totalFr = parts.reduce((a, b) => a + b, 0);
  let currentPos = 0;
  return parts.map(fr => {
    const size = (fr / totalFr) * totalSize;
    const start = currentPos;
    currentPos += size;
    return { start, size };
  });
};

const getLayoutRects = (layout, width, height) => {
  if (!layout) return [];

  const cols = parseGridTemplate(layout.gridTemplateColumns, width);
  const rows = parseGridTemplate(layout.gridTemplateRows, height);

  const cellMap = {};

  layout.areas.forEach((rowStr, rowIndex) => {
    const cells = rowStr.trim().split(/\s+/);
    cells.forEach((areaName, colIndex) => {
      if (!cellMap[areaName]) {
        cellMap[areaName] = { minC: colIndex, maxC: colIndex, minR: rowIndex, maxR: rowIndex };
      } else {
        cellMap[areaName].minC = Math.min(cellMap[areaName].minC, colIndex);
        cellMap[areaName].maxC = Math.max(cellMap[areaName].maxC, colIndex);
        cellMap[areaName].minR = Math.min(cellMap[areaName].minR, rowIndex);
        cellMap[areaName].maxR = Math.max(cellMap[areaName].maxR, rowIndex);
      }
    });
  });

  // Return rects in order 'a', 'b', 'c', 'd'
  return ["a", "b", "c", "d"]
    .filter(area => cellMap[area]) // Filter out unused areas (e.g. if 3 photos but layout supports 4? Actually layouts are specific)
    .map(area => {
      const { minC, maxC, minR, maxR } = cellMap[area];
      const x = cols[minC].start;
      const y = rows[minR].start;
      const right = cols[maxC].start + cols[maxC].size;
      const bottom = rows[maxR].start + rows[maxR].size;
      return { x, y, w: right - x, h: bottom - y };
    });
};


/* ---------------- HELPER COMPONENTS ---------------- */

const CollageImageSlot = ({
  photo,
  index,
  onTransform,
  onRemove,
  onEdit,
  isLoadingEditor,
  editingPhotoIndex,
  onSwap
}) => {
  const containerRef = useRef(null);
  const [onDragIndex, setOnDragIndex] = useState(null);
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });



  const [isDragOver, setIsDragOver] = useState(false);

  // Measure container
  useEffect(() => {
    if (!containerRef.current) return;

    const observeTarget = containerRef.current;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });

    resizeObserver.observe(observeTarget);
    return () => resizeObserver.disconnect();
  }, []);

  // Determine styles for "Cover" fit while allowing overflow
  const containerRatio = containerSize.width / containerSize.height;
  const imgRatio = imgSize.width / imgSize.height;

  // If image is wider than container (relative to height) -> Fit Height, Overflow Width
  // else -> Fit Width, Overflow Height
 const isWide = imgRatio > containerRatio;

const imgStyle = {
  width: isWide ? "auto" : "100%",
  height: isWide ? "100%" : "auto",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "block"
};



  return (
    <div
      ref={containerRef}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const sourceIndex = parseInt(e.dataTransfer.getData("photoIndex"));
        if (!isNaN(sourceIndex)) {
          onSwap(sourceIndex, index);
        }
      }}
      style={{
        gridArea: ["a", "b", "c", "d"][index],
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: 8,
        backgroundColor: "#f3f4f6",
        border: isDragOver ? "3px dashed #6D28D9" : "none",
        transition: "border 0.2s"
      }}
    >
      {/* Only render TransformWrapper if we have valid measurements to avoid jump */}
      {containerSize.width > 0 && (
        <TransformWrapper
          centerOnInit={true}
          initialScale={1}
          minScale={0.5} // Allow some zoom out but effectively bounded by logic usually
          maxScale={4}
          wheel={{ step: 0.1 }}
          // Important: Disable bounds check if we want to allow panning "empty" areas? 
          // Better: limitToBounds={true} is default. With our styling, the content > wrapper, so panning works.
          onTransformed={(ref, state) => onTransform(index, state)}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{ width: "100%", height: "100%" }}
            >
              <img
                src={photo.editedPreview || cleanUrl(photo.thumbnail_url || photo.original_url || photo.url)}
                alt={`Selected ${index + 1}`}
                style={imgStyle}
                onLoad={(e) => setImgSize({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
              />
            </TransformComponent>
          )}
        </TransformWrapper>
      )}

      <div className="absolute top-2 left-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center pointer-events-none z-10">
        <span className="text-white text-xs font-bold">{index + 1}</span>
      </div>

      <div
        className="absolute top-1 left-9 w-6 h-6 bg-white/90 hover:bg-white rounded-md flex items-center justify-center z-10 cursor-move shadow-sm active:scale-95 transition-all"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("photoIndex", index);
        }}
        title="Drag to swap"
      >
        <Move className="w-3 h-3 text-gray-700" />
      </div>

      <Button
        variant="ghost"
        size="icon"
        disabled={isLoadingEditor}
        className="absolute top-1 right-1 w-6 h-6 bg-white/90 hover:bg-white disabled:opacity-50 z-10"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(photo);
        }}
      >
        <X className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        disabled={isLoadingEditor}
        className="absolute bottom-1 right-1 w-6 h-6 bg-white/90 hover:bg-white disabled:opacity-50 z-10"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(index);
        }}
        title="Edit Photo"
      >
        {isLoadingEditor && editingPhotoIndex === index ? (
          <Loader2 className="w-4 h-4 text-gray-700 animate-spin" />
        ) : (
          <Edit className="w-3 h-3 text-gray-700" />
        )}
      </Button>
    </div>
  );
};

const cleanUrl = (url = '') => url.replace(/\\/g, '');

const MultiPhotoCollage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const urlPatientId = searchParams.get('patientId');
  const [selectedPatientId, setSelectedPatientId] = useState(urlPatientId || '');
  const [search, setSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const patientSelectRef = useRef(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [collageTitle, setCollageTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [imageTransforms, setImageTransforms] = useState({});
  const previewContainerRef = useRef(null);

  const handleSwapPhotos = (sourceIndex, targetIndex) => {
    if (sourceIndex === targetIndex) return;

    const newPhotos = [...selectedPhotos];
    const sourcePhoto = newPhotos[sourceIndex];
    newPhotos[sourceIndex] = newPhotos[targetIndex];
    newPhotos[targetIndex] = sourcePhoto;

    setSelectedPhotos(newPhotos);

    // Swap Transforms
    setImageTransforms(prev => {
      const sourceTransform = prev[sourceIndex];
      const targetTransform = prev[targetIndex];

      const next = { ...prev };
      if (sourceTransform) next[targetIndex] = sourceTransform;
      else delete next[targetIndex];

      if (targetTransform) next[sourceIndex] = targetTransform;
      else delete next[sourceIndex];

      return next;
    });
  };

  // Photo Editor State
  const [showEditor, setShowEditor] = useState(false);
  const [editingPhotoIndex, setEditingPhotoIndex] = useState(null);
  const [currentEditingImage, setCurrentEditingImage] = useState(null);
  const [isLoadingEditor, setIsLoadingEditor] = useState(false);


  // Hooks - must be declared before useEffect that uses them
  const debouncedSearch = useDebounce(search, 500);
  const { data: patientListData, isLoading: isLoadingPatientList } = usePatientList(debouncedSearch, {
    enabled: isSearching && debouncedSearch.length > 0,
  });
  const patientList = patientListData?.data || [];

  // Get patient photos when patient is selected
  const { data: patientData, isLoading: isLoadingPatientPhotos } = usePatientProfile(selectedPatientId);

  const patient = patientData?.data || {};
  const photos = patient?.photos || [];

  const { mutate: createMultiPhotoCollage, isPending: isCreatingMultiPhotoCollage } = useCreateMultiPhotoCollage();

  useEffect(() => {
    if (urlPatientId && patient?.full_name && !search) {
      setSearch(patient.full_name);
      setIsSearching(false);
      setShowPatientDropdown(false);

    }
  }, [urlPatientId, patient?.full_name, search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (patientSelectRef.current && !patientSelectRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };

    if (showPatientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPatientDropdown]);


  useEffect(() => {
    if (selectedPhotos.length > 0) {
      setActivePreviewIndex(0);
    }

    // Default layout selection
    if (selectedPhotos.length === 3) {
      setSelectedLayout(collageLayouts.three[0]);
    } else if (selectedPhotos.length === 4) {
      setSelectedLayout(collageLayouts.four[0]);
    }
  }, [selectedPhotos]);


  useEffect(() => {
    if (!selectedPatientId) {
      setSelectedPhotos([]);
      setCollageTitle('');
    }
  }, [selectedPatientId]);

  const handleSelectPatient = (patient) => {
    setSelectedPatientId(String(patient.id));
    setSearch(patient.full_name || patient.fullName || '');
    setShowPatientDropdown(false);
    setIsSearching(false);
    setShowPatientDropdown(false);
    setIsSearching(false);
    setSelectedPhotos([]);
    setImageTransforms({});
    setCollageTitle('');
  };

  const cleanUrl = (url = '') => url.replace(/\\/g, '');

  const handlePhotoSelect = (photo) => {
    const photoId = photo.photo_id || photo.id;

    const exists = selectedPhotos.some(
      (p) => (p.photo_id || p.id) === photoId
    );

    if (exists) {
      setSelectedPhotos(
        selectedPhotos.filter(
          (p) => (p.photo_id || p.id) !== photoId
        )
      );
      // Clean up transform state for this photo (optional, but good practice)
      const newTransforms = { ...imageTransforms };
      // Note: Index based keys might shift if we filter array. 
      // Better to just clear all if we remove one? Or we just accept indices might mismatch until full clear.
      // For simplicity, we'll keep transforms as is, user can re-adjust if they re-add.
      return;
    }

    if (selectedPhotos.length >= 4) {
      toast.error('You can select maximum 4 photos');
      return;
    }

    setSelectedPhotos([...selectedPhotos, photo]);
  };

  const generateMultiPhotoCollage = async (photos) => {
    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });

    const images = await Promise.all(
      photos.map((p) =>
        loadImage(cleanUrl(p.editedPreview || p.original_url || p.url))
      )
    );

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Standard size for higher quality
    canvas.width = 2000;
    canvas.height = 2000;

    // Fill white background first
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (selectedLayout) {
      const rects = getLayoutRects(selectedLayout, canvas.width, canvas.height);

      // We need to know the DOM size of the preview slots to map the transforms correctly.
      // Since we don't have individual refs for every slot easily accessible in this async function,
      // we can estimate or rely on the container width if the preview is square.
      // Let's assume the preview container has a known width in the DOM.
      let previewContainerWidth = 400; // Fallback
      if (previewContainerRef.current) {
        previewContainerWidth = previewContainerRef.current.offsetWidth;
      }

      // Calculate scale ratio between Canvas and Preview Container
      const globalScale = canvas.width / previewContainerWidth;

      images.forEach((img, index) => {
        if (index < rects.length) {
          const { x, y, w, h } = rects[index];
          const transform = imageTransforms[index];

          // Calculate "Cover" dimensions to match DOM behavior
          const imgRatio = img.width / img.height;
          const targetRatio = w / h;

          let renderW, renderH;

          if (imgRatio > targetRatio) {
            // Image is wider: Height is 100%, Width is Auto (overflows)
            renderH = h;
            renderW = h * imgRatio;
          } else {
            // Image is taller: Width is 100%, Height is Auto (overflows)
            renderW = w;
            renderH = w / imgRatio;
          }

          // Calculate translations
          // If transform exists, use it (scaled). 
          // If not, assume default centering (mimicking centerOnInit behavior).
          // Note: react-zoom-pan-pinch 'positionX' is the total translation from 0,0.

          let tx, ty, scale;

          if (transform) {
            tx = transform.positionX * globalScale;
            ty = transform.positionY * globalScale;
            scale = transform.scale;
          } else {
           if (!transform) {
  scale = 1;
  tx = (w - renderW) / 2;
  ty = (h - renderH) / 2;
}

          }

          ctx.save();
          ctx.beginPath();
          ctx.rect(x, y, w, h);
          ctx.clip();

          // Move to slot origin
          ctx.translate(x, y);
          // Apply Pan and Zoom
          ctx.translate(tx, ty);
          ctx.scale(scale, scale);

          // Draw image at 0,0 relative to the translated origin
          // (Since tx includes the offset from 0,0 of the slot)
          ctx.drawImage(img, 0, 0, renderW, renderH);

          ctx.restore();

          // Draw border
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 20;
          ctx.strokeRect(x, y, w, h);
        }
      });
    } else {
      // Fallback simple grid if no layout selected (shouldn't happen)
      if (images.length === 3) {
        // ... (existing fallback logic kept effectively by else, but actually we rely on valid selectedLayout)
      }
    }


    /* ---------------- EXPORT (NO COMPRESSION) ---------------- */
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(
            new File([blob], 'collage.jpg', {
              type: 'image/jpeg',
            })
          );
        },
        'image/jpeg', // ❌ no quality param → browser default (no compression)
      );
    });
  };


  const handleCreateCollage = async () => {
    if (!selectedPatientId) {
      toast.error('Please select a patient');
      return;
    }

    if (selectedPhotos.length < 3 || selectedPhotos.length > 4) {
      toast.error('Select minimum 3 and maximum 4 photos');
      return;
    }

    if (!collageTitle.trim()) {
      toast.error('Please provide collage title');
      return;
    }

    try {
      setIsGenerating(true);
      const collageFile = await generateMultiPhotoCollage(selectedPhotos);

      const formData = new FormData();
      formData.append('patient_id', selectedPatientId);
      formData.append('collage_title', collageTitle);

      selectedPhotos.forEach((photo) => {
        formData.append(
          'photo_ids[]',
          photo.photo_id || photo.id
        );
      });

      formData.append('collage_image', collageFile);

      createMultiPhotoCollage(formData, {
        onSuccess: () => {
          toast.success('Progress collage created successfully');
          navigate(`/patients/${selectedPatientId}`);
        },
        onError: (err) => {
          toast.error(
            err.response?.data?.message || 'Failed to create collage'
          );
        },
        onSettled: () => {
          setIsGenerating(false);
          setSelectedPhotos([]);
          setCollageTitle('');
          setSelectedPatientId('');
          setShowPatientDropdown(false);
          setIsSearching(false);
          setIsGenerating(false);
        },
      });
    } catch (err) {
      setIsGenerating(false);
      console.error(err);
      toast.error('Failed to generate collage image');
    }
  };

  /* ---------------- PHOTO EDITOR HANDLERS ---------------- */

  const handleEditPhoto = async (index) => {
    try {
      setIsLoadingEditor(true);
      const photo = selectedPhotos[index];
      // Use existing edited preview if available, otherwise original
      const imageUrl = photo.editedPreview || cleanUrl(photo.original_url || photo.url || photo.thumbnail_url);

      // Fetch the image and convert to Blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${index}.jpg`, { type: blob.type || 'image/jpeg' });

      setCurrentEditingImage(file);
      setEditingPhotoIndex(index);
      setShowEditor(true);
    } catch (error) {
      console.error('Error loading image:', error);
      toast.error('Failed to load image for editing');
    } finally {
      setIsLoadingEditor(false);
    }
  };

  const handleSaveEditedPhoto = (editedFile) => {
    // Create a preview URL from the edited file
    const previewUrl = URL.createObjectURL(editedFile);

    // Update the selected photo with the edited preview
    const updatedPhotos = [...selectedPhotos];
    updatedPhotos[editingPhotoIndex] = {
      ...updatedPhotos[editingPhotoIndex],
      editedPreview: previewUrl,
    };

    setSelectedPhotos(updatedPhotos);
    setShowEditor(false);
    setEditingPhotoIndex(null);
    setCurrentEditingImage(null);
    toast.success('Photo edited successfully');
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingPhotoIndex(null);
    setCurrentEditingImage(null);
  };


  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selection Panel */}
        <div className=" space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select Patient & Photos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Patient <span className="text-xs text-red-500">*</span></Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="patient-search"
                    placeholder="Search for a patient"
                    value={search}
                    onFocus={() => setIsSearching(true)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearch(value);

                      // Clear selected patient when input is empty
                      if (!value.trim()) {
                        setSelectedPatientId('');
                        setShowPatientDropdown(false);
                        setIsSearching(false);
                        return;
                      }

                      setShowPatientDropdown(true);
                      setIsSearching(true);
                    }}
                    className="pl-10"
                  />
                  {selectedPatientId && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                </div>


                {/* Patient Dropdown */}
                {showPatientDropdown && (
                  <div
                    ref={patientSelectRef}
                    className="z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    {isLoadingPatientList ? (
                      <div className="p-4 text-center text-gray-500">Loading patients...</div>
                    ) : patientList.length > 0 ? (
                      patientList.map((patient) => (
                        <div
                          key={patient.id}
                          onClick={() => handleSelectPatient(patient)}
                          className={`p-3 cursor-pointer hover:bg-gray-100 transition-colors ${String(selectedPatientId) === String(patient.id) ? 'bg-primary-50' : ''
                            }`}
                        >
                          <div className="font-medium text-gray-900">{patient.full_name || 'N/A'}</div>
                          {patient.patient_uid && (
                            <div className="text-xs text-gray-500">ID: {patient.patient_uid}</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">No patients found</div>
                    )}
                  </div>
                )}
              </div>

              {isLoadingPatientPhotos && selectedPatientId && (
                <div className="text-center py-4 text-gray-500">
                  <p>Loading patient photos...</p>
                </div>
              )}

              {selectedPatientId && !isLoadingPatientPhotos && photos.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No photos found for this patient.</p>
                  <Button
                    variant="link"
                    onClick={() => navigate(`/capture?patientId=${selectedPatientId}`)}
                    className="text-primary-600"
                  >
                    Capture photos first
                  </Button>
                </div>
              )}

              {photos.length > 0 && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Select Photos (3-4) <span className="text-xs text-red-500">*</span></Label>
                      <span className="text-sm text-gray-500">
                        {selectedPhotos.length} / 4 selected
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {photos.map((photo) => {
                        const photoId = photo.photo_id || photo.id;
                        const isSelected = selectedPhotos.find(p => (p.photo_id || p.id) === photoId);
                        const selectionIndex = selectedPhotos.findIndex(p => (p.photo_id || p.id) === photoId);
                        return (
                          <div
                            key={photoId}
                            className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${isSelected
                              ? 'border-primary-500 ring-2 ring-primary-200'
                              : 'border-gray-200 hover:border-primary-300'
                              }`}
                            onClick={() => handlePhotoSelect(photo)}
                          >
                            <img
                              src={cleanUrl(photo.thumbnail_url || photo.original_url || photo.url)}
                              alt="Photo"
                              className="w-full h-full object-cover"
                            />
                            {isSelected && (
                              <>
                                <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                                <div className="absolute top-2 left-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs font-bold">
                                    {selectionIndex + 1}
                                  </span>
                                </div>
                              </>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                              <p className="text-white text-xs">{formatDate(photo.captured_at || photo.capturedAt)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview & Actions */}
        <div className=" space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-primary-600" />
                Preview
              </CardTitle>
                <div className="space-y-2">
                     {(selectedPhotos.length === 3 || selectedPhotos.length === 4) && (
                    <div className="p-4 border rounded-xl flex flex-col gap-2 bg-gray-50/50">
                    <Label className="">Select Layout</Label>
                      <LayoutSelector
                        layouts={selectedPhotos.length === 3 ? collageLayouts.three : collageLayouts.four}
                        onSelect={setSelectedLayout}
                        selectedLayoutId={selectedLayout?.id}
                      />
                    </div>
                     )}
                  </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedPhotos.length >= 3 ? (
                <>
                  <div
                    ref={previewContainerRef}
                    style={{
                      display: "grid",
                      width: "100%",
                      aspectRatio: "1 / 1",
                      gap: 8,
                      gridTemplateColumns: selectedLayout?.gridTemplateColumns,
                      gridTemplateRows: selectedLayout?.gridTemplateRows,
                      gridTemplateAreas: selectedLayout?.areas?.map(r => `"${r}"`).join(" ")
                    }}
                  >
                    {selectedPhotos.map((photo, index) => (
                      <CollageImageSlot
                        key={photo.photo_id || photo.id}
                        photo={photo}
                        index={index}
                        onTransform={(idx, state) => {
                          setImageTransforms(prev => ({
                            ...prev,
                            [idx]: { scale: state.scale, positionX: state.positionX, positionY: state.positionY }
                          }));
                        }}
                        onRemove={(p) => handlePhotoSelect(p)}
                        onEdit={(idx) => handleEditPhoto(idx)}
                        isLoadingEditor={isLoadingEditor}
                        editingPhotoIndex={editingPhotoIndex}
                        onSwap={handleSwapPhotos}
                      />
                    ))}
                  </div>

                

                  <div className="space-y-2">
                    <Label>Collage Title <span className="text-xs text-red-500">*</span></Label>
                    <Input
                      placeholder="Enter collage title"
                      value={collageTitle}
                      onChange={(e) => setCollageTitle(e.target.value)}
                    />
                  </div>

                  <Button
                    onClick={handleCreateCollage}
                    disabled={isGenerating || isCreatingMultiPhotoCollage}
                    className={`w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white
    ${(isGenerating || isCreatingMultiPhotoCollage) ? 'opacity-50 cursor-not-allowed' : ''}
  `}
                  >
                    {isGenerating
                      ? 'Generating Image...'
                      : isCreatingMultiPhotoCollage
                        ? 'Creating Collage...'
                        : 'Create Collage'}
                  </Button>

                </>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <LayoutGrid className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">Select 3-4 photos to create collage</p>
                </div>
              )}
            </CardContent>
          </Card>


        </div>
      </div>

      {/* Loading Overlay */}
      {isLoadingEditor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4">
            <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Switching to Edit Mode
              </h3>
              <p className="text-sm text-gray-500">
                Preparing your photo for editing...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Photo Editor Modal */}
      {showEditor && currentEditingImage && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">


          <div className="p-4">
            <ReactPhotoEditor
              open={showEditor}
              onClose={handleCloseEditor}
              file={currentEditingImage}
              onSaveImage={handleSaveEditedPhoto}
            />
          </div>

        </div>
      )}
    </div>
  );
};

export default MultiPhotoCollage;